// TipTapEditor — admin's blog editor for `location_tasks.instruction_doc`.
//
// ProseMirror JSON in/out — round-trippable, future-proof for re-editing.
// Toolbar: bold / italic / strike / heading L1+L2 / bullet list / ordered list /
// blockquote / horizontal rule / link / image (R2 upload) / undo / redo.
//
// Image upload uploads to /v1/uploads/image (R2 via tenant-api LAYOUTS_BUCKET)
// and inserts the resulting public URL into the doc. R2 path:
//   tasks/instructions/<random ulid>.<ext>
//
// Renders inline styling that matches the dashboard's dark theme tokens.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import {
  Bold, Italic, Strikethrough, List, ListOrdered, Quote, Minus,
  Heading1, Heading2, Link as LinkIcon, Image as ImageIcon, Undo, Redo,
} from "lucide-react";
import { apiFetch, API_BASE } from "../../lib/api-client.js";
import { useConfirmDialog } from "./ConfirmDialog.js";

interface Props {
  /** Stored ProseMirror JSON as string (or null on first edit). */
  value: string | null;
  onChange: (json: string) => void;
  /** Optional tenant override for R2 image upload routing. */
  tenantId?: string | null;
  /** Disable editing (preview / archived task). */
  readOnly?: boolean;
  placeholder?: string;
}

export default function TipTapEditor({ value, onChange, tenantId, readOnly = false, placeholder }: Props): React.ReactElement {
  const initialContent = parseInitial(value);
  const dlg = useConfirmDialog();

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        bulletList: { keepMarks: true },
        orderedList: { keepMarks: true },
      }),
      Image.configure({
        inline: false,
        HTMLAttributes: { class: "rounded-md max-w-full" },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { class: "text-accent underline" },
      }),
    ],
    editable: !readOnly,
    content: initialContent ?? "",
    onUpdate: ({ editor }) => {
      onChange(JSON.stringify(editor.getJSON()));
    },
    editorProps: {
      attributes: {
        class: "prose-location min-h-[280px] p-4 outline-none text-sm leading-relaxed",
      },
    },
  });

  // Sync external value changes (e.g. switching between tasks in the same drawer).
  const lastValueRef = useRef<string | null>(value);
  useEffect(() => {
    if (!editor) return;
    if (value === lastValueRef.current) return;
    lastValueRef.current = value;
    editor.commands.setContent(parseInitial(value) ?? "", { emitUpdate: false });
  }, [editor, value]);

  if (!editor) {
    return <div className="rounded-md border border-border bg-bg-raised p-4 text-xs text-text-muted">載入編輯器…</div>;
  }

  return (
    <div className="rounded-md border border-border bg-bg-raised flex flex-col">
      {!readOnly && <Toolbar editor={editor} tenantId={tenantId} />}
      <style>{PROSE_CSS}</style>
      <EditorContent editor={editor} />
      {placeholder && !value && (
        <p className="px-4 pb-3 text-2xs text-text-muted">{placeholder}</p>
      )}
    </div>
  );
}

// ── Toolbar ──────────────────────────────────────────────────────────────

function Toolbar({ editor, tenantId }: { editor: NonNullable<ReturnType<typeof useEditor>>; tenantId: string | null | undefined }): React.ReactElement {
  const [busy, setBusy] = useState(false);
  const dlg = useConfirmDialog();

  const onLink = useCallback(() => {
    const prev = editor.getAttributes("link").href as string | undefined;
    const url = window.prompt("連結 URL（留空清除）", prev ?? "https://");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    if (!/^https?:\/\//i.test(url) && !url.startsWith("/")) {
      void dlg.alert("URL 必須以 http:// 或 https:// 開頭，或以 / 開頭的相對路徑。", { title: "URL 格式錯誤" });
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url, target: "_blank", rel: "noopener noreferrer" }).run();
  }, [editor]);

  const onImage = useCallback(async () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/png,image/jpeg,image/webp,image/gif";
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      if (file.size > 5 * 1024 * 1024) {
        await dlg.alert("圖片大於 5 MB，請壓縮後再上傳。", { title: "檔案過大" });
        return;
      }
      setBusy(true);
      try {
        const fd = new FormData();
        fd.set("file", file);
        const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
        const r = await apiFetch(`/v1/uploads/image${qs}`, { method: "POST", body: fd, credentials: "include" });
        if (!r.ok) {
          const t = await r.text().catch(() => `HTTP ${r.status}`);
          await dlg.alert(`上傳失敗：${t.slice(0, 200)}`, { title: "上傳失敗" });
          return;
        }
        const j = await r.json() as { url?: string };
        if (!j.url) { await dlg.alert("伺服器未回 URL", { title: "上傳失敗" }); return; }
        // Server returns a path like `/v1/uploads/image/<ulid>.png`. Without a
        // host that resolves against the dashboard origin (nexus.sentry.red)
        // — which has no /v1/* routes — and the <img> just shows broken. Anchor
        // it at API_BASE so the resulting `<img src>` points at api.location.
        // External URLs (rare, but possible if upstream changes) pass through.
        const src = j.url.startsWith("http") ? j.url : `${API_BASE}${j.url}`;
        editor.chain().focus().setImage({ src }).run();
      } finally { setBusy(false); }
    };
    input.click();
  }, [editor, tenantId]);

  return (
    <div className="flex flex-wrap items-center gap-0.5 border-b border-border bg-bg-surface px-2 py-1.5">
      <BtnGroup>
        <ToolBtn active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()} title="粗體 (Ctrl+B)"><Bold className="h-3.5 w-3.5" /></ToolBtn>
        <ToolBtn active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()} title="斜體 (Ctrl+I)"><Italic className="h-3.5 w-3.5" /></ToolBtn>
        <ToolBtn active={editor.isActive("strike")} onClick={() => editor.chain().focus().toggleStrike().run()} title="刪除線"><Strikethrough className="h-3.5 w-3.5" /></ToolBtn>
      </BtnGroup>
      <Sep />
      <BtnGroup>
        <ToolBtn active={editor.isActive("heading", { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} title="標題 1"><Heading1 className="h-3.5 w-3.5" /></ToolBtn>
        <ToolBtn active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} title="標題 2"><Heading2 className="h-3.5 w-3.5" /></ToolBtn>
      </BtnGroup>
      <Sep />
      <BtnGroup>
        <ToolBtn active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()} title="無序列表"><List className="h-3.5 w-3.5" /></ToolBtn>
        <ToolBtn active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="有序列表"><ListOrdered className="h-3.5 w-3.5" /></ToolBtn>
        <ToolBtn active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()} title="引用"><Quote className="h-3.5 w-3.5" /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().setHorizontalRule().run()} title="分隔線"><Minus className="h-3.5 w-3.5" /></ToolBtn>
      </BtnGroup>
      <Sep />
      <BtnGroup>
        <ToolBtn active={editor.isActive("link")} onClick={onLink} title="連結"><LinkIcon className="h-3.5 w-3.5" /></ToolBtn>
        <ToolBtn onClick={() => void onImage()} disabled={busy} title="插入圖片"><ImageIcon className="h-3.5 w-3.5" /></ToolBtn>
      </BtnGroup>
      <Sep />
      <BtnGroup>
        <ToolBtn onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="復原 (Ctrl+Z)"><Undo className="h-3.5 w-3.5" /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="重做 (Ctrl+Shift+Z)"><Redo className="h-3.5 w-3.5" /></ToolBtn>
      </BtnGroup>
      <div className="ml-auto text-2xs text-text-muted px-1">{busy ? "上傳中…" : ""}</div>
    </div>
  );
}

function ToolBtn({ children, active, disabled, onClick, title }: {
  children: React.ReactNode;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  title?: string;
}): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`h-7 w-7 inline-flex items-center justify-center rounded ${
        active ? "bg-accent/15 text-accent" : "text-text-secondary hover:text-text-primary hover:bg-bg-hover"
      } disabled:opacity-30 disabled:hover:bg-transparent`}
    >
      {children}
    </button>
  );
}
function BtnGroup({ children }: { children: React.ReactNode }): React.ReactElement {
  return <div className="flex gap-0.5">{children}</div>;
}
function Sep(): React.ReactElement {
  return <span className="w-px h-5 bg-border mx-1.5" />;
}

// ── helpers ──────────────────────────────────────────────────────────────

/** Parse stored ProseMirror JSON. Loose typed as a JSON tree literal —
 *  TipTap accepts the JSONContent shape. We cast at use-site rather than
 *  importing JSONContent to avoid the type-only import. */
function parseInitial(value: string | null): Record<string, unknown> | undefined {
  if (!value) return undefined;
  try {
    const doc = JSON.parse(value) as Record<string, unknown>;
    // Stored TipTap JSON may carry image src as `/v1/uploads/image/<ulid>.png`
    // (relative path). The dashboard origin has no /v1/* routes; left as-is
    // the browser resolves the src against nexus.sentry.red and the
    // image renders broken. Rewrite to absolute API_BASE on read so older
    // saved docs render correctly without a DB migration.
    rewriteRelativeImageSrcs(doc);
    return doc;
  }
  catch { return undefined; }
}

function rewriteRelativeImageSrcs(node: unknown): void {
  if (!node || typeof node !== "object") return;
  const n = node as { type?: string; attrs?: { src?: string }; content?: unknown[] };
  if (n.type === "image" && n.attrs?.src && n.attrs.src.startsWith("/v1/")) {
    n.attrs.src = `${API_BASE}${n.attrs.src}`;
  }
  if (Array.isArray(n.content)) {
    for (const child of n.content) rewriteRelativeImageSrcs(child);
  }
}

const PROSE_CSS = `
  .prose-location h1 { font-size: 1.5rem; font-weight: 700; margin: 1rem 0 .5rem; color: #e8e8ec; }
  .prose-location h2 { font-size: 1.25rem; font-weight: 700; margin: .9rem 0 .4rem; color: #e8e8ec; }
  .prose-location h3 { font-size: 1.1rem; font-weight: 600; margin: .8rem 0 .3rem; color: #e8e8ec; }
  .prose-location p  { margin: .35rem 0; color: #e8e8ec; }
  .prose-location ul { list-style: disc;     padding-left: 1.5rem; margin: .35rem 0; }
  .prose-location ol { list-style: decimal;  padding-left: 1.5rem; margin: .35rem 0; }
  .prose-location li { margin: .15rem 0; }
  .prose-location blockquote {
    border-left: 3px solid #00d4ff; padding-left: .9rem;
    color: #9b9ba1; margin: .6rem 0;
  }
  .prose-location hr { border: none; border-top: 1px solid #2a2a35; margin: 1rem 0; }
  .prose-location code {
    background: rgba(0,212,255,.12); color: #00d4ff;
    padding: .1rem .3rem; border-radius: 3px; font-size: .85em;
  }
  .prose-location pre {
    background: #0a0a0c; padding: .75rem; border-radius: 6px;
    overflow-x: auto; margin: .6rem 0;
  }
  .prose-location pre code { background: transparent; color: #e8e8ec; padding: 0; }
  .prose-location a { color: #00d4ff; text-decoration: underline; }
  .prose-location img { max-width: 100%; border-radius: .5rem; margin: .6rem 0; }
  .prose-location :first-child { margin-top: 0; }
  .prose-location :last-child { margin-bottom: 0; }
`;
